'use client';

import { useState } from 'react';
import Link from 'next/link';
import AuthBar from '@/components/AuthBar';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';

export default function LoginPage() {
  const { language, user } = useAppStore();
  const t = translate(language);
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const error = login.error as Error | null;

  if (user) {
    return (
      <div className="mx-auto w-full max-w-[420px] px-6 pt-16">
        <AuthBar />
        <div className="card p-8 text-center">
          <h1 className="mb-3 text-xl font-bold">{t.auth.welcomeBack}</h1>
          <p className="mb-6 text-sm text-gf-muted">{t.auth.alreadyLoggedIn}</p>
          <div className="flex flex-col gap-2">
            <Link href="/markets" className="btn-ghost !h-10">
              {t.auth.backToMarkets}
            </Link>
            <Link href="/profile" className="btn-primary !h-10">
              {t.auth.goToAccount}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[420px] px-6 pt-4 sm:pt-6">
      <AuthBar />
      <div className="card p-6 sm:p-8">
        <h1 className="mb-1 text-center text-2xl font-bold">{t.auth.welcomeBack}</h1>
        <p className="mb-6 text-center text-sm text-gf-muted">{t.auth.loginHint}</p>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            login.mutate({ email, password });
          }}
        >
          <div>
            <label htmlFor="login-email" className="mb-1 block text-sm text-gf-muted">
              {t.auth.email}
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input !h-10 !text-base"
              placeholder={t.auth.emailPlaceholder}
              required
            />
          </div>
          <div>
            <label htmlFor="login-password" className="mb-1 block text-sm text-gf-muted">
              {t.auth.password}
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input !h-10 !pr-10 !text-base"
                placeholder={t.auth.passwordPlaceholder}
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1.5 py-1 text-xs text-gf-muted hover:text-gf-text"
                aria-label={showPwd ? 'Hide password' : 'Show password'}
              >
                {showPwd ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-gf-negative">{error.message}</p>}
          <button type="submit" className="btn-primary !h-10" disabled={login.isPending}>
            {login.isPending ? t.auth.loggingIn : t.auth.login}
          </button>
        </form>
        <div className="mt-4 text-center text-sm text-gf-muted">
          <a href="/register" className="text-gf-accent hover:underline">
            {t.auth.noAccount}
          </a>
        </div>
      </div>
    </div>
  );
}
